// ─── Positionskalkulation ────────────────────────────────────────────────────
// Reine Funktion: Bestandteile + Zuschläge rein, EP/GP + Rechenweg raus.
// Das LLM schlägt höchstens Bestandteile und Mengen vor — gerechnet wird hier.

import { d, summe } from './dezimal.js';

export const KOSTENARTEN = ['LOHN', 'MATERIAL', 'GERAET', 'FREMDLEISTUNG', 'SONSTIGES'];

export const KOSTENART_LABEL = {
  LOHN: 'Lohn',
  MATERIAL: 'Material',
  GERAET: 'Gerät',
  FREMDLEISTUNG: 'Fremdleistung',
  SONSTIGES: 'Sonstiges',
};

// Minuten je Zeiteinheit. Lohn und Gerät MÜSSEN in einer Zeiteinheit stehen.
const ZEIT_MINUTEN = {
  h: 60, std: 60, 'std.': 60, stunde: 60, stunden: 60,
  min: 1, 'min.': 1, minute: 1, minuten: 1,
};

export function minutenJeEinheit(einheit) {
  return ZEIT_MINUTEN[String(einheit || '').trim().toLowerCase()] ?? null;
}

const ZEITBASIERT = new Set(['LOHN', 'GERAET']);

// Default für § 35a: Lohn und Gerät sind Arbeitskosten, Material nicht.
export function istArbeitskosten(bestandteil) {
  if (typeof bestandteil.arbeitskosten_35a === 'boolean') return bestandteil.arbeitskosten_35a;
  return ZEITBASIERT.has(bestandteil.kostenart);
}

function zuschlagFuer(kostenart, bestandteil, pos, profil) {
  const override = pos.zuschlaege?.[kostenart];
  if (override && override.prozent !== undefined && override.prozent !== null && override.prozent !== '') {
    return { prozent: d(override.prozent), quelle: 'Position' };
  }
  if (bestandteil.zuschlag_prozent !== undefined && bestandteil.zuschlag_prozent !== null && bestandteil.zuschlag_prozent !== '') {
    return { prozent: d(bestandteil.zuschlag_prozent), quelle: bestandteil.zuschlag_quelle || 'Bestandteil' };
  }
  const p = profil?.[kostenart];
  return { prozent: d(p ?? 0), quelle: 'Zuschlagsprofil' };
}

/**
 * @param pos {
 *   menge, modus: 'EINHEITSPREIS' | 'GESAMTPREIS',
 *   kalkulation_aktiv (default true), vk_fest,
 *   bestandteile: [{ kostenart, beschreibung, menge, einheit, ek_je_einheit,
 *                    zuschlag_prozent?, arbeitskosten_35a? }],
 *   zuschlaege: { [kostenart]: { prozent } | { betrag } },   // Override an der Position
 *   rabatt_prozent
 * }
 * @param kontext { zuschlagsprofil: { LOHN: 12, MATERIAL: 20, ... } }
 */
export function berechnePosition(pos, kontext = {}) {
  const menge = d(pos.menge ?? 1);
  const modus = pos.modus === 'GESAMTPREIS' ? 'GESAMTPREIS' : 'EINHEITSPREIS';
  const rechenweg = [];
  const fehler = [];

  if (menge.istNegativ()) fehler.push('Die Menge darf nicht negativ sein.');

  // ── Festpreis ohne Kalkulation (z. B. Leistung aus alten Rechnungen) ──
  if (pos.kalkulation_aktiv === false || !pos.bestandteile?.length) {
    const ep = d(pos.vk_fest ?? pos.einzelpreis ?? 0).runde(2);
    const epNetto = rabattiert(ep, pos.rabatt_prozent);
    const gp = epNetto.mal(menge).runde(2);
    rechenweg.push({ schritt: 'Festpreis je Einheit', wert: ep.alsZahl(2) });
    if (!epNetto.gleich(ep)) rechenweg.push({ schritt: `Rabatt ${pos.rabatt_prozent} %`, wert: epNetto.alsZahl(2) });
    rechenweg.push({ schritt: `GP = ${epNetto.alsText(2)} × ${menge.alsText(3)}`, wert: gp.alsZahl(2) });
    return {
      modus: 'FESTPREIS', ep: epNetto, ep_vor_rabatt: ep, gp,
      ek_je_einheit: leereKostenarten(), vk_je_einheit: leereKostenarten(),
      ek_summe_je_einheit: d(0), vk_summe_je_einheit: epNetto,
      arbeitskosten_je_einheit: d(0), zeitbedarf_min: d(0), soll_minuten: d(0),
      mischzuschlag_prozent: null, rundungsdifferenz: d(0), rechenweg, fehler,
    };
  }

  if (modus === 'GESAMTPREIS' && menge.istNull()) {
    fehler.push('Im Gesamtpreis-Modus muss die Menge größer als 0 sein.');
  }

  const ek = leereKostenarten();
  const vk = leereKostenarten();
  let arbeitskosten = d(0);
  let lohnMinuten = d(0);
  const zeilen = [];

  for (const b of pos.bestandteile) {
    const ka = KOSTENARTEN.includes(b.kostenart) ? b.kostenart : 'SONSTIGES';
    const bMenge = d(b.menge ?? 0);
    const ekJe = d(b.ek_je_einheit ?? 0);
    let ekGesamt;
    let minuten = null;

    if (ZEITBASIERT.has(ka)) {
      const mje = minutenJeEinheit(b.einheit);
      if (mje === null) {
        fehler.push(`${b.beschreibung || ka}: ${KOSTENART_LABEL[ka]} braucht eine Zeiteinheit (h oder min), nicht "${b.einheit || '–'}".`);
        continue;
      }
      minuten = bMenge.mal(mje);
      // erst multiplizieren, dann durch 60 — bleibt exakt (150 min × 56 €/h = 140,00)
      ekGesamt = minuten.mal(ekJe).durch(60);
      if (ka === 'LOHN') lohnMinuten = lohnMinuten.plus(minuten);
    } else {
      ekGesamt = bMenge.mal(ekJe);
    }

    const z = zuschlagFuer(ka, b, pos, kontext.zuschlagsprofil);
    const vkGesamt = ekGesamt.mal(d(1).plus(z.prozent.durch(100)));

    ek[ka] = ek[ka].plus(ekGesamt);
    vk[ka] = vk[ka].plus(vkGesamt);
    if (istArbeitskosten({ ...b, kostenart: ka })) arbeitskosten = arbeitskosten.plus(vkGesamt);

    zeilen.push({
      kostenart: ka, beschreibung: b.beschreibung || '', menge: bMenge.alsZahl(3), einheit: b.einheit || '',
      ek_je_einheit: ekJe.alsZahl(4), ek: ekGesamt.alsZahl(4), zuschlag_prozent: z.prozent.alsZahl(4),
      zuschlag_quelle: z.quelle, vk: vkGesamt.alsZahl(4), minuten: minuten ? minuten.alsZahl(3) : null,
    });
  }

  // Zuschlag als Betrag (€) an der Position: VK_k = EK_k + Betrag
  for (const ka of KOSTENARTEN) {
    const o = pos.zuschlaege?.[ka];
    if (o && o.betrag !== undefined && o.betrag !== null && o.betrag !== '' && (o.prozent === undefined || o.prozent === null || o.prozent === '')) {
      const alt = vk[ka];
      vk[ka] = ek[ka].plus(d(o.betrag));
      if (ZEITBASIERT.has(ka)) arbeitskosten = arbeitskosten.minus(alt).plus(vk[ka]);
    }
  }

  const ekSumme = summe(Object.values(ek));
  const vkSumme = summe(Object.values(vk));

  // Bezug "je Einheit": im Gesamtpreis-Modus durch die Menge teilen
  const jeEinheit = (x) => (modus === 'GESAMTPREIS' ? (menge.istNull() ? d(0) : x.durch(menge)) : x);

  const epVorRabatt = jeEinheit(vkSumme).runde(2);
  const ep = rabattiert(epVorRabatt, pos.rabatt_prozent);
  const gp = ep.mal(menge).runde(2);

  // Rabatt mindert alle Kostenart-Anteile im selben Verhältnis
  const rabattFaktor = rabattFaktorVon(pos.rabatt_prozent);
  const vkJeEinheit = leereKostenarten();
  const ekJeEinheit = leereKostenarten();
  for (const ka of KOSTENARTEN) {
    vkJeEinheit[ka] = jeEinheit(vk[ka]).mal(rabattFaktor);
    ekJeEinheit[ka] = jeEinheit(ek[ka]);
  }
  const arbeitskostenJeEinheit = jeEinheit(arbeitskosten).mal(rabattFaktor);

  const zeitbedarf = jeEinheit(lohnMinuten);
  const rundungsdifferenz = modus === 'GESAMTPREIS' && !pos.rabatt_prozent
    ? gp.minus(vkSumme.runde(2))
    : d(0);

  const mischzuschlag = ekSumme.istNull() ? null : vkSumme.minus(ekSumme).durch(ekSumme).mal(100);

  rechenweg.push(...zeilen.map((z) => ({
    schritt: `${KOSTENART_LABEL[z.kostenart]}: ${z.beschreibung} — ${fmt(z.menge, 3)} ${z.einheit} × ${fmt(z.ek_je_einheit, 2)} € ${z.minuten !== null ? '/h ' : ''}= ${fmt(z.ek, 2)} € EK, +${fmt(z.zuschlag_prozent, 2)} % (${z.zuschlag_quelle})`,
    wert: Math.round(z.vk * 100) / 100,
  })));
  rechenweg.push({ schritt: modus === 'GESAMTPREIS' ? `EP = ${vkSumme.alsText(2)} ÷ ${menge.alsText(3)}` : 'EP = Σ VK', wert: epVorRabatt.alsZahl(2) });
  if (!ep.gleich(epVorRabatt)) rechenweg.push({ schritt: `Rabatt ${pos.rabatt_prozent} %`, wert: ep.alsZahl(2) });
  rechenweg.push({ schritt: `GP = ${ep.alsText(2)} × ${menge.alsText(3)}`, wert: gp.alsZahl(2) });
  if (!rundungsdifferenz.istNull()) rechenweg.push({ schritt: 'Rundungsdifferenz zur Summe der Bestandteile', wert: rundungsdifferenz.alsZahl(2) });

  return {
    modus, ep, ep_vor_rabatt: epVorRabatt, gp,
    ek_je_einheit: ekJeEinheit, vk_je_einheit: vkJeEinheit,
    ek_summe_je_einheit: jeEinheit(ekSumme), vk_summe_je_einheit: jeEinheit(vkSumme).mal(rabattFaktor),
    ek_kostenart_gesamt: ek, vk_kostenart_gesamt: vk,
    arbeitskosten_je_einheit: arbeitskostenJeEinheit,
    zeitbedarf_min: zeitbedarf,
    soll_minuten: zeitbedarf.mal(menge),
    mischzuschlag_prozent: mischzuschlag,
    rundungsdifferenz,
    bestandteile: zeilen,
    rechenweg,
    fehler,
  };
}

function rabattFaktorVon(rabatt) {
  if (!rabatt) return d(1);
  return d(1).minus(d(rabatt).durch(100));
}

function rabattiert(ep, rabatt) {
  if (!rabatt) return ep;
  return ep.mal(rabattFaktorVon(rabatt)).runde(2);
}

function leereKostenarten() {
  return Object.fromEntries(KOSTENARTEN.map((k) => [k, d(0)]));
}

function fmt(x, stellen) {
  return Number(x).toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
}

// Minuten → "h:mm"
export function alsStunden(minuten) {
  const m = Math.round(d(minuten).alsZahl(3));
  const neg = m < 0;
  const a = Math.abs(m);
  return `${neg ? '−' : ''}${Math.floor(a / 60)}:${String(a % 60).padStart(2, '0')}`;
}
